import axiosInstance from "./axiosInstance";
import { getUserInfoDetails } from "../utlis";
import { formatDate } from "../utlis";
import { store } from "../store/store";

const { companyId, clientId } = getUserInfoDetails()

// Validate URL - only allow http/https protocols
const sanitizeUrl = (url) => {
  if (!url) return url;
  try {
    const parsed = new URL(url);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') return url;
    return undefined; // Reject non-http URLs (javascript:, data:, etc.)
  } catch {
    return url; // Allow non-URL strings (could be a meeting code)
  }
};

// Helper to strip phone formatting to raw digits
const stripPhone = (phone) => {
  if (!phone) return '';
  return String(phone).replace(/\D/g, '');
};

// Lookup technology name (division) from config by technology_id
const lookupTechnologyName = (technologyId) => {
  if (!technologyId) return undefined;
  try {
    const refData = store.getState().reference;
    const techs = refData?.technologies?.technology || [];
    const match = techs.find(t => String(t.technology_id) === String(technologyId));
    return match ? match.name : undefined;
  } catch (e) {
    return undefined;
  }
};

// Build API-compliant payload from UI state
const buildCandidatePayload = (parseBody) => {
  // Resolve talentId from technology (technology_id from config as string, convert to integer)
  const technologyValue = parseBody.technology;
  const talentId = technologyValue ? parseInt(technologyValue, 10) : undefined;
  const division = lookupTechnologyName(technologyValue);

  const payload = {
    // Required fields
    firstName: parseBody.firstName,
    lastName: parseBody.lastName,
    email: parseBody.email,
    status: parseBody.status,

    // Basic info
    middleName: parseBody.middleName,
    gender: parseBody.gender,
    dateOfBirth: formatDate(parseBody.dob) || undefined,
    maritalStatus: parseBody.maritalStatus || undefined,
    mobile: stripPhone(parseBody.phone) || undefined,
    phone: stripPhone(parseBody.alternatePhoneNum) || undefined,
    address1: parseBody.address1 || '',
    address2: parseBody.address2 || '',
    city: parseBody.city || '',
    state: parseBody.state || '',
    zip: parseBody.zip || '',

    // Professional info - map UI names to API names
    jobTitle: parseBody.jobTitle,
    salesPersonId: parseBody.salesPerson ? +parseBody.salesPerson : undefined,
    legalStatus: parseBody.workPermitStatus,
    workStatusIssueDate: formatDate(parseBody.workStatusIssueDate) || undefined,
    workStatusEndDate: formatDate(parseBody.workStatusEndDate) || undefined,
    SSN: parseBody.SSN ? String(parseBody.SSN).replace(/\D/g, '') : undefined,
    employmentStatus: parseBody.employmentStatus,
    referredBy: parseBody.referredBy || '',
    talentId: talentId,
    division: division,

    // Profile info
    rating: parseBody.rating || 0,
  };

  // Remove undefined values
  Object.keys(payload).forEach(key => {
    if (payload[key] === undefined) delete payload[key];
  });

  return payload;
};

// POST Request
export const createCandidate = async (parseBody) => {
  const payload = buildCandidatePayload(parseBody);
  return axiosInstance.post(`/candidates?client=${clientId}&company=${companyId}`, payload);
};

export const updateCandidate = async (parseBody, candidateID) => {
  const payload = buildCandidatePayload(parseBody);
  return axiosInstance.put(`/candidates/${candidateID}?client=${clientId}&company=${companyId}`, payload);
};

export const searchCandidates = async (searchText, pageNum = 1, pageSize = 100) => {
  return axiosInstance.post(
    `/candidates/searches?client=${clientId}&company=${companyId}&pageNum=${pageNum}&pageSize=${pageSize}`,
    { searchText }
  );
};

// Get candidate by ID
export const getCandidateById = async (candidateID) => {
  // Get user info inside function to ensure it's available
  const userInfo = getUserInfoDetails() || {};
  const { clientId: cId, companyId: coId } = userInfo;
  return axiosInstance.get(`/candidates/${candidateID}?client=${cId}&company=${coId}`);
};

export const createMarketingHistory = async (data, candidateID) => {
  return axiosInstance.post(`/marketing?client=${clientId}&company=${companyId}`, {
    candidateId: String(candidateID),
    phone: stripPhone(data.phone),
    email: data.email,
    startDate: formatDate(data.marketingStartDate),
    endDate: data.marketingEndDate ? formatDate(data.marketingEndDate) : "",
    status: data.marketingStatus,
    salesPersonId: data.salesPersonId ? +data.salesPersonId : undefined,
    salesManagerId: data.salesManagerId ? +data.salesManagerId : undefined,
    linkedin: data.linkedInID,
    skype: data.skypeID,
  });
};

export const createNote = async (notes, genericId, recordType) => {
  return axiosInstance.post(`/notes?client=${clientId}&company=${companyId}`, {
    genericId: +genericId,
    notes,
    recordType
  });
};

export const createMarketingList = async (data, candidateID) => {
  return axiosInstance.post(`/marketing?client=${clientId}&company=${companyId}`, {
    candidateId: String(candidateID),
    salesPersonId: data.salesPerson ? +data.salesPerson : undefined,
    salesManagerId: data.salesManager ? +data.salesManager : undefined,
    startDate: formatDate(data.salesStartDate),
    endDate: data.salesEndDate ? formatDate(data.salesEndDate) : "",
    totalJobsApplied: +data.jobsApplied || 0,
    totalInterviewsAttended: +data.interviewsAttended || 0,
    status: data.marketingStatus
  });
};


export const createMarketingJob = async (data, candidateID) => {
  return axiosInstance.post(`/jobs?client=${clientId}&company=${companyId}&candidateId=${candidateID}`, {
    candidateId: String(candidateID),
    jobTitle: data.jobTitle,
    vendorId: +data.vendorId,
    vendorContactId: +data.vendor_contact_id,
    salesPersonId: +data.sales_person_id,
    technologyId: +data.technology,
    endClientName: data.client,
    payType: data.payType,
    billRate: +data.billRate,
    noOfOpenings: +data.numOfPositions,
    duration: +data.durationMonths,
    jobDesc: data.jobDescription,
    city: data.city,
    state: data.state,
    statusReason: data.statusReason,
    status: data.status || 'OPEN',
  });
};

export const updateMarketingJob = async (data, candidateID) => {
  return axiosInstance.put(`/jobs/${data.jobId}?client=${clientId}&company=${companyId}&candidateId=${candidateID}`, {
    candidateId: +candidateID,
    jobTitle: data.jobTitle,
    vendorId: +data.vendorId,
    vendorContactId: +data.vendor_contact_id,
    salesPersonId: +data.sales_person_id,
    technologyId: +data.technology,
    endClientName: data.client,
    payType: data.payType,
    billRate: +data.billRate,
    noOfOpenings: +data.numOfPositions,
    duration: +data.durationMonths,
    jobDesc: data.jobDescription,
    city: data.city,
    state: data.state,
    statusReason: data.statusReason,
    status: data.status,
  });
};


export const createJobInterview = async (data, candidateID) => {
  return axiosInstance.post(`/interview?client=${clientId}&company=${companyId}`, {
    candidateId: candidateID,
    interviewDate: formatDate(data.interviewDate),
    vendorId: data.vendorName || 2,
    //inteviewTime: data.interviewTime,
    appointmentLink: data.appointmentDetailsLink,
    interviewPanelDetails: data.panelDetails,
    feedback: data.feedback,
    rating: String(data.rating)
  });
};

// Timezone options with UTC offsets
const timezoneOptions = {
  "EST": "-05:00",
  "CST": "-06:00",
  "MST": "-07:00",
  "PST": "-08:00",
  "AST": "-09:00",
  "HST": "-10:00",
};

// Helper to convert 12-hour time to 24-hour format
const convertTo24Hour = (time, amPm) => {
  if (!time) return { hours: '00', minutes: '00' };
  const timeParts = time.split(':');
  let hours = parseInt(timeParts[0]) || 0;
  const minutes = timeParts[1] || '00';

  if (amPm === 'PM' && hours !== 12) {
    hours = hours + 12;
  } else if (amPm === 'AM' && hours === 12) {
    hours = 0;
  }

  return {
    hours: String(hours).padStart(2, '0'),
    minutes: String(minutes).padStart(2, '0')
  };
};

// Helper to format time to HH:MM:SS+ZZ:ZZ format with timezone
const formatInterviewTime = (time, timezone, amPm) => {
  if (!time) return null;
  // Convert 12-hour time to 24-hour format
  const { hours, minutes } = convertTo24Hour(time, amPm || 'AM');
  const seconds = '00';

  // Get timezone offset from selected timezone, default to CST
  const tzOffset = timezoneOptions[timezone] || "-06:00";

  return `${hours}:${minutes}:${seconds}${tzOffset}`;
};

export const createInterview = async (data, candidateID) => {
  // Get user info inside function to ensure it's available
  const userInfo = getUserInfoDetails() || {};
  const { clientId: cId, companyId: coId } = userInfo;

  const payload = {
    // Required fields (integers)
    candidateId: +candidateID,
    vendorId: +data.vendorId,
    jobId: +data.jobId,
  };

  // Optional fields - values are already formatted by the component
  if (data.division) payload.division = data.division;
  if (data.interviewDate) payload.interviewDate = data.interviewDate;
  if (data.interviewTime) payload.interviewTime = data.interviewTime;
  if (data.schedulingStatus) payload.schedulingStatus = data.schedulingStatus;
  if (data.appointmentLink) payload.appointmentLink = sanitizeUrl(data.appointmentLink);
  if (data.appointmentLink2) payload.appointmentLink2 = sanitizeUrl(data.appointmentLink2);
  if (data.roundCount) payload.roundCount = +data.roundCount;
  if (data.interviewPanelDetails) payload.interviewPanelDetails = data.interviewPanelDetails;
  if (data.appointmentDetails) payload.appointmentDetails = data.appointmentDetails;
  if (data.feedback) payload.feedback = data.feedback;
  if (data.rating) payload.rating = data.rating ? data.rating.toString() : "0";
  if (data.salesPersonId) payload.salesPersonId = +data.salesPersonId;
  if (data.masterJobId) payload.masterJobId = +data.masterJobId;
  if (data.marketingId) payload.marketingId = +data.marketingId;
  if (data.status) payload.status = data.status;

  return axiosInstance.post(`/interview?client=${cId}&company=${coId}`, payload);
};

export const updateInterview = async (data, candidateID) => {
  // Get user info inside function to ensure it's available
  const userInfo = getUserInfoDetails() || {};
  const { clientId: cId, companyId: coId } = userInfo;

  const payload = {
    // Required fields (integers)
    candidateId: +candidateID,
    vendorId: +data.vendorId,
    jobId: +data.jobId,
  };

  // Optional fields - values are already formatted by the component
  if (data.division) payload.division = data.division;
  if (data.interviewDate) payload.interviewDate = data.interviewDate;
  if (data.interviewTime) payload.interviewTime = data.interviewTime;
  if (data.schedulingStatus) payload.schedulingStatus = data.schedulingStatus;
  if (data.appointmentLink) payload.appointmentLink = sanitizeUrl(data.appointmentLink);
  if (data.appointmentLink2) payload.appointmentLink2 = sanitizeUrl(data.appointmentLink2);
  if (data.roundCount) payload.roundCount = +data.roundCount;
  if (data.interviewPanelDetails) payload.interviewPanelDetails = data.interviewPanelDetails;
  if (data.appointmentDetails) payload.appointmentDetails = data.appointmentDetails;
  if (data.feedback) payload.feedback = data.feedback;
  if (data.rating) payload.rating = data.rating ? data.rating.toString() : "0";
  if (data.salesPersonId) payload.salesPersonId = +data.salesPersonId;
  if (data.masterJobId) payload.masterJobId = +data.masterJobId;
  if (data.marketingId) payload.marketingId = +data.marketingId;
  if (data.status) payload.status = data.status;

  return axiosInstance.put(`/interview/${data.interviewId}?client=${cId}&company=${coId}`, payload);
};

// Build project payload from form data
// Safe integer conversion - returns undefined instead of NaN
const toInt = (val) => {
  if (!val && val !== 0) return undefined;
  const n = Number(val);
  return isNaN(n) ? undefined : n;
};

const buildProjectPayload = (data) => {
  const payload = {
    // Status (required)
    status: data.projectStatus || data.status,
    // Project info
    endClientName: data.endClientName,
    division: data.division,
    remoteWork: data.remoteWork,
    // Vendor & Sales
    vendorId: toInt(data.vendorId),
    vendorContactId: toInt(data.vendorContactId),
    salesMgrId: toInt(data.salesMgrId),
    hrContactId: toInt(data.hrContactId),
    accountingContactId: toInt(data.accountingContactId),
    // Location
    city: data.city,
    state: data.state,
    // Dates
    startDate: formatDate(data.startDate),
    estimatedEndDate: formatDate(data.estimatedEndDate),
    // Billing
    billingRate: toInt(data.billingRate),
    payType: data.payType,
    paymentTerms: data.paymentTerms,
    // Invoice
    firstInvoiceDate: formatDate(data.firstInvoiceDate),
    lastInvoiceDate: formatDate(data.lastInvoiceDate),
    nextInvoiceDate: formatDate(data.nextInvoiceDate),
    invoiceContactEmail: data.invoiceContactEmail,
    // Background process
    backgroundProcessDesc: data.backgroundProcessDesc,
    backgroundProcessType: data.backgroundProcessType,
    backgroundStartOn: formatDate(data.backgroundStartOn),
    backgroundEndOn: formatDate(data.backgroundEndOn),
    backgroundStatus: data.backgroundStatus,
    // Related IDs
    jobId: toInt(data.jobId),
    interviewId: toInt(data.interviewId),
    marketingId: toInt(data.marketingId),
    clientId: toInt(data.clientId),
  };

  // Remove undefined values
  Object.keys(payload).forEach(key => {
    if (payload[key] === undefined) delete payload[key];
  });

  return payload;
};

export const createProject = async (data, candidateID) => {
  const userInfo = getUserInfoDetails() || {};
  const { clientId: cId, companyId: coId } = userInfo;

  const payload = buildProjectPayload(data);
  const cid = Number(candidateID);
  if (isNaN(cid)) throw new Error('Invalid candidateId: ' + candidateID);
  payload.candidateId = cid;

  return axiosInstance.post(`/projects?client=${cId}&company=${coId}`, payload);
};

export const updateProject = async (data, projectId) => {
  const userInfo = getUserInfoDetails() || {};
  const { clientId: cId, companyId: coId } = userInfo;

  const payload = buildProjectPayload(data);
  if (data.candidateId) payload.candidateId = +data.candidateId;

  return axiosInstance.put(`/projects/${projectId}?client=${cId}&company=${coId}`, payload);
};

export const createTraining = async (data, candidateID) => {
  return axiosInstance.post(`/training?client=${clientId}&company=${companyId}`, {
    candidateId: candidateID,
    clientName: data.clientName,
    status: data.trainingStatus,
    vendor: data.vendor,
    city: data.city,
    startDate: formatDate(data.startDate),
    endDate: formatDate(data.endDate)
  });
};

export const updateTraining = async (data, trainingId) => {
  // Get user info inside function to ensure it's available
  const userInfo = getUserInfoDetails() || {};
  const { clientId: cId, companyId: coId } = userInfo;

  return axiosInstance.put(`/training/${trainingId}?client=${cId}&company=${coId}`, {
    candidateId: data.candidateId,
    clientName: data.clientName,
    status: data.trainingStatus || data.status,
    vendor: data.vendor,
    city: data.city,
    startDate: formatDate(data.startDate),
    endDate: formatDate(data.endDate)
  });
};

export const createDocment = async (data, candidateID) => {
  return axiosInstance.post(`/document?client=${clientId}&company=${companyId}`, {
    candidateId: candidateID,
    documentName: data.documentName,
    link: data.link,
    description: data.description,
    createdDate: formatDate(data.createdDate),
    updatedDate: formatDate(data.updatedDate),
  });
};

export const fetchMarketingList = (additionalParam) => {
  return axiosInstance.get('/marketing', {
    params: { company: companyId, client: clientId, ...additionalParam }
  });
}

export const fetchMarketingById = (recordId, candidateId) => {
  return axiosInstance.get(`/marketing/${recordId}`, {
    params: { company: companyId, client: clientId, ...(candidateId ? { candidateId } : {}) }
  });
}

export const updateMarketing = (recordId, data) => {
  return axiosInstance.put(`/marketing/${recordId}?client=${clientId}&company=${companyId}`, data);
}

export const fetchJobList = (additionalParam) => {
  return axiosInstance.get('/jobs', {
    params: { company: companyId, client: clientId, ...additionalParam }
  });
}

export const fetchProjectList = (additionalParam) => {
  return axiosInstance.get('/projects', {
    params: { company: companyId, client: clientId, ...additionalParam }
  });
}

export const fetchTrainingList = (additionalParam) => {
  return axiosInstance.get('/training', {
    params: { company: companyId, client: clientId, ...additionalParam }
  });
}

export const getAllSalesPerson = () => {
  return axiosInstance.get('/salesteam', {
    params: { company: companyId, client: clientId }
  })
}

const ContactType = {
  1: 'primary',
  2: 'secondary'
};

export const createEmergencyContact = (contactInfo, candidateId, sequence) => {

  return axiosInstance.post('/emergencycontact', {
    sequence,
    candidateId: candidateId.toString(),
    "firstName": contactInfo.firstName,
    "type": ContactType[sequence],
    "lastName": contactInfo.lastName,
    "email": contactInfo.email,
    "phone": {
      "phone": stripPhone(contactInfo.contactPhoneNum),
    },
    "relation": contactInfo.contactRelation,
    status: "ACTIVE"
  })
}

export const getEmergencyContact = (candidateId) => {
  // Get user info inside function to ensure it's available
  const userInfo = getUserInfoDetails() || {};
  const { clientId: cId, companyId: coId } = userInfo;
  return axiosInstance.get(`/emergencycontact/${candidateId}`, {
    params: { company: coId, client: cId }
  });
};

export const updateEmergencyContact = (contactInfo, candidateId, sequence) => {

  return axiosInstance.put('/emergencycontact', {
    sequence,
    candidateId: candidateId.toString(),
    "firstName": contactInfo.firstName,
    "type": ContactType[sequence],
    "lastName": contactInfo.lastName,
    "email": contactInfo.email,
    "phone": {
      "phone": stripPhone(contactInfo.contactPhoneNum),
    },
    "relation": contactInfo.contactRelation,
    status: "ACTIVE"
  })
}

export const getAllNotes = (additionalParam) => {
  return axiosInstance.get('/notes', {
    params: {
      company: companyId, client: clientId, ...additionalParam
    }
  })
}